import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isDev } from "../../config/environment";
import * as yup from "yup";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { useTSMResetPassword } from "../../hooks/useTSAuth";

// Validation schema
const loginSchema = yup.object({
  password: yup.string().min(6).required("Password is Required"),
  confirm_password: yup
    .string()
    .required("Please confirm your password")
    .test("passwords-match", "Passwords must match", function (value) {
      return value === this.parent.password;
    }),
});

interface LoginFormData {
  password: string;
  confirm_password: string;
}

export const PagePasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") || "";
  const [error, setError] = useState<string | null>(null);

  // Get the intended destination from location state
  const from = "/login";

  const resetPWMutation = useTSMResetPassword();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      password: "",
      confirm_password: "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      await resetPWMutation.mutateAsync({ token, password: data.password });
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(
        err.message ||
          "Password Reset Failed. Please return to the login page and request another password reset email or contact IT if this issue persists."
      );
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: "100%",
            maxWidth: 400,
          }}
        >
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ textAlign: "center" }}>
              <img
                src="/nucleus.svg"
                alt="Nucleus Logo"
                style={{ width: "100%", height: "auto" }}
              />
              {/* <Typography variant="h4" component="h1" gutterBottom>
                NUCLEUS
              </Typography> */}
              <Typography variant="h6" color="text.secondary">
                Reset Your Password
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <TextField
                  sx={{ display: "none" }}
                  id="username"
                  label="username"
                  fullWidth
                  autoComplete="username"
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="new-password"
                      label="Password"
                      type="password"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      slotProps={{
                        input: { autoComplete: "new-password" },
                        htmlInput: { autoComplete: "new-password" },
                      }}
                    />
                  )}
                />
                <Controller
                  name="confirm_password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="confirm-password"
                      label="Password"
                      type="password"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      slotProps={{
                        input: { autoComplete: "new-password" },
                        htmlInput: { autoComplete: "new-password" },
                      }}
                    />
                  )}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Sending Password Reset Request...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </Stack>
            </Box>

            {/* Footer */}
            {/* <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Secure login with cookie-based authentication
              </Typography>
            </Box> */}
          </Stack>
        </Paper>

        {/* Development Info */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            {isDev ? "!!!DEVELOPMENT ENVIRONMENT!!!" : ""}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};
